import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  areAllMultiFamilyTierMaxUnitsUnique,
  doesExactlyOneMultiFamilyTierHaveNullMaxUnits,
  isITieredUnitCountConfiguration,
  sortMultiFamilyTiers,
} from "@llws/hydra-shared";
import { Nullable } from "@jamesgmarks/utilities";
import { ISubscriptions } from "@llws/lift-entity-interfaces";
import { showMessage } from "src/redux/features/messaging/actions";
import {
  TCustomConfiguration, CustomConfigSchema, TTieredUnitCountConfiguration, TieredUnitCountConfigSchema, TUnitOfMeasure, TChargeStrategy,
  TMFTier,
  MFTierSchema,
} from "./customConfigMeta";
import { JsonRenderer } from "src/components/pages/Subscriptions/EditorComponents/JsonRenderer";
import { SingleFamilyTier } from "src/components/pages/Subscriptions/tiered-unit-count/SingleFamilyTier";
import { MultiFamilyTier } from "src/components/pages/Subscriptions/tiered-unit-count/MultiFamilyTier";
import { MultiFamilyTierComponent } from "src/components/pages/Subscriptions/form/CustomConfigEditor/TieredUnitEditorComponents/MultiFamilyTierComponent";
import { SingleFamilyTierComponent } from "src/components/pages/Subscriptions/form/CustomConfigEditor/TieredUnitEditorComponents/SingleFamilyTierComponent";

interface ITieredUnitCountConfigurationAccordionProps {
  isEditingSubscriptionConfiguration: boolean;
  setIsEditingSubscriptionConfiguration: React.Dispatch<React.SetStateAction<boolean>>;
  baseSubscriptionConfiguration: Nullable<TCustomConfiguration>;
  subscriptionConfiguration: Nullable<TCustomConfiguration>;
  setCustomConfiguration: (config: TCustomConfiguration) => void;
  storeSubscription: Nullable<ISubscriptions>;
  hasErrors?: boolean;
}

export const TieredUnitCountConfigurationAccordion: React.FC<ITieredUnitCountConfigurationAccordionProps> = ({
  isEditingSubscriptionConfiguration,
  setIsEditingSubscriptionConfiguration,
  baseSubscriptionConfiguration,
  subscriptionConfiguration,
  setCustomConfiguration,
  storeSubscription,
}) => {
  const baseConfig = useMemo(() => {
    const parsedConfig = CustomConfigSchema.safeParse(baseSubscriptionConfiguration);
    if (parsedConfig.success) {
      return parsedConfig.data.tieredUnitCountConfig;
    }
    return null;
  }, [baseSubscriptionConfiguration]);
  const currentConfig = useMemo(() => {
    const parsedConfig = CustomConfigSchema.safeParse(subscriptionConfiguration);
    if (parsedConfig.success) {
      return parsedConfig.data.tieredUnitCountConfig;
    }
    return null;
  }, [subscriptionConfiguration]);

  const onCopyBaseSubscriptionConfiguration = () => {
    const baseConfig = CustomConfigSchema.safeParse(baseSubscriptionConfiguration);
    if (baseConfig.success) {
      setCustomConfiguration(baseConfig.data);
    }
  };

  const onCopyOriginalSubscriptionConfiguration = () => {
    const config = CustomConfigSchema.safeParse(storeSubscription?.customConfiguration);
    if (config.success) {
      setCustomConfiguration(config.data);
    }
  };

  return (
    <Grid container spacing={1} textAlign="center">
      <Grid item xs={isEditingSubscriptionConfiguration ? 5 : 6}>
        <Typography variant="h6" textAlign="center" mb={2}>
          Base subscription configuration
        </Typography>

        {baseConfig ? (
          <JsonRenderer
            obj={{ ...baseConfig, MF: baseConfig.MF.slice().sort(sortMultiFamilyTiers) }}
            keyReplacements={{ SF: "SF", MF: "MF" }}
            dollarValueKeys={["pricePerUnit", "pricePerProperty"]}
            customStyles={{ SF: { marginTop: 20 } }}
          />
        ) : (
          <Alert icon={false} color="warning" sx={{ justifyContent: "center" }}>
            <i>No tiered unit configuration in base subscription.</i>
          </Alert>
        )}
      </Grid>

      <Grid item xs={isEditingSubscriptionConfiguration ? 7 : 6}>
        <Typography variant="h6" textAlign="center" mb={2}>
          <b>Subscription configuration to save...</b>
        </Typography>

        {isEditingSubscriptionConfiguration && currentConfig ? (
          <TieredUnitCountConfigurationEditor
            config={currentConfig}
            onSave={(updatedConfig) => {
              const config = {
                ...subscriptionConfiguration,
                tieredUnitCountConfig: { ...updatedConfig },
              };

              setCustomConfiguration(config);
              setIsEditingSubscriptionConfiguration(false);
            }}
            onCancel={() => setIsEditingSubscriptionConfiguration(false)}
            onInvalidConfiguration={() => setIsEditingSubscriptionConfiguration(false)}
          />
        ) : currentConfig ? (
          <Box>
            <JsonRenderer
              obj={{ ...currentConfig, MF: currentConfig.MF.slice().sort(sortMultiFamilyTiers) }}
              backgroundColor="#d3f3ff"
              keyReplacements={{ SF: "SF", MF: "MF" }}
              dollarValueKeys={["pricePerUnit", "pricePerProperty"]}
              customStyles={{ SF: { marginTop: 20 } }}
            />
            <Grid item xs={12} mt={2}>
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={() => setIsEditingSubscriptionConfiguration(true)}
              >
                Edit
              </Button>
            </Grid>
          </Box>
        ) : (
          <Alert icon={false} color="warning" sx={{ justifyContent: "center" }}>
            <i>No tiered unit configuration in subscription.</i>
          </Alert>
        )}

        <Grid item xs={12} mt={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={onCopyOriginalSubscriptionConfiguration}
            disabled={!isITieredUnitCountConfiguration(storeSubscription?.customConfiguration?.tieredUnitCountConfig)}
          >
            Use last-saved subscription configuration
          </Button>
        </Grid>

        <Grid item xs={12} my={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={onCopyBaseSubscriptionConfiguration}
            disabled={!isITieredUnitCountConfiguration(baseConfig)}
          >
            Copy base subscription configuration
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface IConfigurationEditorProps {
  config: TTieredUnitCountConfiguration;
  onCancel: () => void;
  onSave: (updatedConfiguration: TTieredUnitCountConfiguration) => void;
  onInvalidConfiguration?: () => void;
}

export const TieredUnitCountConfigurationEditor: React.FC<IConfigurationEditorProps> = ({
  config,
  onCancel,
  onSave,
  onInvalidConfiguration,
}) => {
  const augmentedMultiFamilyTiers = useMemo(
    () => config.MF.map((tier, i) => ({ id: i, ...tier })),
    [config],
  );

  const [nextId, setNextId] = useState(augmentedMultiFamilyTiers.length);
  const [draftConfig, setDraftConfig] = useState({
    ...config,
    MF: augmentedMultiFamilyTiers,
  });

  const sortedMultiFamilyTiers = useMemo(() => {
    return draftConfig.MF.slice().sort((a, b) => {
      if (a.maxUnits === null && b.maxUnits === null) return 1;
      if (a.maxUnits === null) return 1;
      if (b.maxUnits === null) return -1;
      return a.maxUnits - b.maxUnits;
    });
  }, [draftConfig]);

  useEffect(() => {
    if (!isITieredUnitCountConfiguration(config)) {
      showMessage({ message: "This subscription configuration is invalid." });
      onInvalidConfiguration?.();
      return;
    }

    setDraftConfig({
      ...config,
      MF: augmentedMultiFamilyTiers,
    });
  }, [config, augmentedMultiFamilyTiers, onInvalidConfiguration]);

  const configurationErrors = useMemo<string[]>(() => [
    ...(!doesExactlyOneMultiFamilyTierHaveNullMaxUnits(draftConfig.MF)
      ? ["Must have exactly 1 MF tier with an empty 'maximum units' (catch-all)"]
      : []),
    ...(!areAllMultiFamilyTierMaxUnitsUnique(draftConfig.MF)
      ? ["'Maximum units' values must be unique across all MF tiers"]
      : []),
    ...(draftConfig.MF.some((tier) => tier.maxUnits === 0)
      ? ["'Maximum units' value cannot be 0 for MF tier"]
      : []),
  ], [draftConfig]);

  return (
    <Grid container justifyContent="center" alignItems="center" pl={1}>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body1" mt={3} mb={1}><u>Single-family</u></Typography>
        </Grid>

        <Grid item xs={12}>
          <SingleFamilyTierComponent
            pricePerUnit={draftConfig.SF.pricePerUnit}
            chargeStrategy={draftConfig.SF.chargeStrategy}
            onChange={(pricePerUnit: number, chargeStrategy: TChargeStrategy) => {
              setDraftConfig((old) => ({
                ...old,
                SF: { ...old.SF, pricePerUnit, chargeStrategy },
              }));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" mt={3} mb={1}><u>Multi-family</u></Typography>
        </Grid>

        {sortedMultiFamilyTiers.map(({ id, pricePerProperty, maxUnits, unitOfMeasure }) => (
          <MultiFamilyTierComponent
            key={id}
            id={id}
            pricePerProperty={pricePerProperty}
            maxUnits={maxUnits}
            unitOfMeasure={unitOfMeasure}
            onDelete={(id: number) => {
              setDraftConfig((old) => ({
                ...old,
                MF: old.MF.filter((tier) => tier.id !== id),
              }));
            }}
            onChange={
              (
                id: number, pricePerProperty: number, maxUnits: Nullable<number>, unitOfMeasure: TUnitOfMeasure,
              ) => {
                setDraftConfig((old) => ({
                  ...old,
                  MF: [
                    ...old.MF.filter((tier) => tier.id !== id),
                    { id, pricePerProperty, maxUnits, unitOfMeasure },
                  ],
                }));
              }

            }
          />
        ))}

        {configurationErrors.map((errorMessage) => (
          <Grid key={errorMessage} item xs={12}>
            <Alert icon={false} color="warning">{errorMessage}</Alert>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={() => {
              setDraftConfig((old) => ({
                ...old,
                MF: [
                  ...old.MF,
                  {
                    id: nextId,
                    pricePerProperty: 0,
                    maxUnits: null,
                    unitOfMeasure: "multi-family",
                  },
                ],
              }));
              setNextId((prev) => prev + 1);
            }}
          >
            Add tier
          </Button>
        </Grid>
      </Grid>

      <Grid item container xs={12} mt={4} justifyContent="center" spacing={2}>
        <Grid item>
          <Button variant="contained" color="error" onClick={onCancel}>
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="success"
            disabled={configurationErrors.length > 0 || !isITieredUnitCountConfiguration(draftConfig)}
            onClick={() => {
              const MF: TMFTier[] = sortedMultiFamilyTiers.map(
                ({ maxUnits, pricePerProperty, unitOfMeasure }) => ({
                  maxUnits,
                  pricePerProperty,
                  unitOfMeasure,
                }),
              );
              onSave({ ...draftConfig, MF });
            }}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
